import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Textarea,
  Button,
  Group,
  Alert,
  Paper,
  Title,
  Badge,
  LoadingOverlay,
  Select
} from '@mantine/core';
import { IconX, IconTrash, IconAlertCircle, IconStatusChange } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  yearlyCalendarSchedulePlanService,
  CourseStatusUpdateData,
  YearlyCalendarCourse
} from 'services/yearlyCalendar/schedulePlanService';

interface CourseStatusModalProps {
  opened: boolean;
  onClose: () => void;
  course: YearlyCalendarCourse;
  planningId: string;
}

const CourseStatusModal: React.FC<CourseStatusModalProps> = ({
  opened,
  onClose,
  course,
  planningId
}) => {
  const [selectedStatus, setSelectedStatus] = useState<'canceled' | 'dropped' | ''>('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  // Status update mutation
  const statusUpdateMutation = useMutation({
    mutationFn: (statusData: CourseStatusUpdateData) =>
      yearlyCalendarSchedulePlanService.updateScheduleCoursePlanEnhanced(statusData),
    onSuccess: (data, variables) => {
      notifications.show({
        title: 'Success',
        message: `Course ${variables.status} successfully`,
        color: variables.status === 'canceled' ? 'red' : 'orange',
        icon: variables.status === 'canceled' ? <IconX size={16} /> : <IconTrash size={16} />
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlanDetail', planningId] });
      queryClient.invalidateQueries({ queryKey: ['yearlyCalendarSchedulePlans'] });

      // Reset form and close modal
      resetForm();
      onClose();
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error?.response?.data?.message || 'Failed to update course status',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
      setIsSubmitting(false);
    }
  });

  const resetForm = () => {
    setSelectedStatus('');
    setNotes('');
    setIsSubmitting(false);
  };

  const handleSubmit = () => {
    if (!selectedStatus || !notes.trim()) {
      notifications.show({
        title: 'Required Fields',
        message: 'Please select a status and provide notes',
        color: 'yellow',
        icon: <IconAlertCircle size={16} />
      });
      return;
    }

    if (notes.trim().length < 10) {
      notifications.show({
        title: 'Invalid Notes',
        message: 'Notes must be at least 10 characters long',
        color: 'yellow',
        icon: <IconAlertCircle size={16} />
      });
      return;
    }

    setIsSubmitting(true);

    const statusData: CourseStatusUpdateData = {
      trLCMSPlanningCoursesID: course.trLCMSPlanningCoursesID,
      status: selectedStatus,
      notes: notes.trim()
    };

    statusUpdateMutation.mutate(statusData);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'canceled':
        return {
          color: 'red',
          icon: <IconX size={16} />,
          label: 'Cancel',
          description:
            'Mark this course as canceled. This course will be removed from the schedule.'
        };
      case 'dropped':
        return {
          color: 'orange',
          icon: <IconTrash size={16} />,
          label: 'Drop',
          description:
            'Mark this course as dropped. This course will be removed from the schedule but may be rescheduled later.'
        };
      default:
        return {
          color: 'gray',
          icon: <IconStatusChange size={16} />,
          label: 'Update',
          description: 'Update the status of this course.'
        };
    }
  };

  const statusConfig = getStatusConfig(selectedStatus);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap='xs'>
          <IconStatusChange size={20} />
          <Text fw={600}>Update Course Status</Text>
        </Group>
      }
      size='md'
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <LoadingOverlay visible={isSubmitting} />

      <Stack gap='md'>
        {/* Course Details */}
        <Paper p='md' withBorder bg='gray.0'>
          <Stack gap='xs'>
            <Group justify='space-between' align='flex-start'>
              <div style={{ flex: 1 }}>
                <Title order={4} lineClamp={2}>
                  {course.courseName}
                </Title>
                <Text size='sm' c='dimmed' mt={2}>
                  {course.courseCode} • {course.learningHours}h
                </Text>
                {course.planningStart && course.planningEnd && (
                  <Text size='sm' c='dimmed' mt='xs'>
                    {new Date(course.planningStart).toLocaleDateString()} -{' '}
                    {new Date(course.planningEnd).toLocaleDateString()}
                  </Text>
                )}
              </div>
              <Badge variant='light' color='blue' size='sm'>
                Currently: {course.statusCourse?.toUpperCase() || 'PLANNED'}
              </Badge>
            </Group>
          </Stack>
        </Paper>

        {/* Status Selection */}
        <Stack gap='xs'>
          <Text size='sm' fw={500}>
            New Status{' '}
            <Text component='span' c='red'>
              *
            </Text>
          </Text>
          <Select
            placeholder='Select new status'
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value as 'canceled' | 'dropped')}
            data={[
              {
                value: 'canceled',
                label: 'Cancel Course',
                disabled: course.statusCourse === 'canceled'
              },
              {
                value: 'dropped',
                label: 'Drop Course',
                disabled: course.statusCourse === 'dropped'
              }
            ]}
            disabled={isSubmitting}
          />
          {selectedStatus && (
            <Alert color={statusConfig.color} variant='light' icon={statusConfig.icon}>
              <Text size='sm'>{statusConfig.description}</Text>
            </Alert>
          )}
        </Stack>

        {/* Notes Input */}
        <Stack gap='xs'>
          <Text size='sm' fw={500}>
            Reason/Notes{' '}
            <Text component='span' c='red'>
              *
            </Text>
          </Text>
          <Textarea
            placeholder='Enter the reason for this status change...'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            minRows={4}
            maxRows={8}
            autosize
            disabled={isSubmitting}
            error={
              notes.length > 0 && notes.trim().length < 10
                ? 'Notes must be at least 10 characters'
                : null
            }
          />
          <Text size='xs' c='dimmed'>
            Minimum 10 characters required
          </Text>
        </Stack>

        {/* Action Buttons */}
        <Group justify='flex-end' mt='md'>
          <Button variant='outline' onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button
            color={statusConfig.color}
            leftSection={statusConfig.icon}
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedStatus || !notes.trim() || notes.trim().length < 10}
            loading={isSubmitting}
          >
            {statusConfig.label} Course
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CourseStatusModal;
